import React, { Suspense, useEffect, useState } from "react";
import { Navigate, Route,Routes, useLocation } from "react-router-dom";  
import { AuthenticatedTemplate } from "@azure/msal-react";
import { RouteGuard } from "./RouteGuard";
import { appRoles } from "./authConfig";
import { SessionTimeout } from "./utils/SessionTimeout";
import AppNavigation from "./shared/AppNavigation/AppNavigation";
import { AppHeader } from "./shared/AppHeader/AppHeader";
import { getAppStyles } from "./App.styles";
import { useIsMobile } from "./utils/ismobile";
import { registerIconsOnce } from "./utils/RegisterIcons";
import { GetUserRole } from "./utils/GetUserRole";
import { isEmpty } from "./utils/OPPApiHelper";
import AppFooter from "./shared/AppFooter/AppFooter";
import { CoherenceLoading } from "@coherence-design-system/controls";
import { initializeIcons } from "@fluentui/react";
import { useThemeMode } from "./contexts/ThemeContext";

// import '@charm-ux/cui/dist/themes/fluent/theme.css';
import '@charm-ux/cui/dist/themes/cui/theme.css';
import CookieConsentBanner from "./utils/CookieConsentBanner";
import MetadataManagement from "./components/MetadataManagement/MetadataManagement";

// Initialize Fluent UI built-in icons (ProductList, ProductCatalog, GridViewMedium, etc.)
// Previously loaded as a side effect of eagerly importing page components
initializeIcons();

// Lazy-loaded route components for code splitting
const NonManaged = React.lazy(() => import("./components/NonManaged/NonManaged"));
const PORDetails = React.lazy(() => import("./components/Managed/Overview/PORDetails/PORDetails"));
const PORItems = React.lazy(() => import("./components/Managed/PORItems/PORItems"));
const ScopeDefinition = React.lazy(() => import("./components/Managed/Overview/ScopeDefinition/ScopeDefinition"));
const ScopeDefinitionDetails = React.lazy(() => import("./components/Managed/Overview/ScopeDefinitionDetails/ScopeDefinitionDetails"));
const DataMonitoring = React.lazy(() => import("./components/Managed/Overview/DataMonitoring/DataMonitoring"));
const OLSOffer = React.lazy(() => import("./components/OLSOffer/OLSOffer"));
const FinanceDataAudit = React.lazy(() => import("./components/FinanceDataAudit/FinanceDataAudit"));
const VLSKUAuditHistory = React.lazy(() => import("./components/VLSKUAudit/VLSKUAuditHistory"));
const VLSKUAudit = React.lazy(() => import("./components/VLSKUAudit/VLSKUAudit"));
const IntakeManagement = React.lazy(() => import("./components/IntakeManagement/IntakeManagement"));
const IntentInputCaptures = React.lazy(() => import("./components/TemplateServices/IntentInputCaptures/IntentInputCaptures"));
const IntentInputCaptureDetails = React.lazy(() => import("./components/TemplateServices/IntentInputCaptureDetails/IntentInputCaptureDetails"));
const ProductConfiguratorList = React.lazy(() => import("./components/ProductConfigurator/ProductConfiguratorList"));
const ProductConfiguratorDetails = React.lazy(() => import("./components/ProductConfigurator/ProductConfiguratorDetails"));
const IntentValidations = React.lazy(() => import("./components/IntentValidations/IntentValidations"));
const UnderDevelopment = React.lazy(() => import("./shared/UnderDevelopment/UnderDevelopment"));
const HeaderSearchResults = React.lazy(() => import("./shared/AppHeader/HeaderSearchResults").then(m => ({ default: m.HeaderSearchResults })));
const Insights = React.lazy(() => import("./components/Insights/Insights"));
const Publish = React.lazy(() => import("./components/Publish/Publish"));
const ReleaseManagement = React.lazy(() => import("./components/ReleaseManagement/TSO/TSODetails"));
const CDDeploymentSummary = React.lazy(() => import("./components/ReleaseManagement/CDDashboard/DeploymentSummary/DeploymentSummary"));
const FixedEvidenceSummary = React.lazy(() => import("./components/ReleaseManagement/CDDashboard/FixedEvidenceSummary/FixedEvidenceSummary"));

// Call registerIconsOnce to ensure icons are registered only once
registerIconsOnce();


declare global {
    interface Window {
        clarity: (event: string, data?: any) => void;
    }
}

export const App: React.FunctionComponent  = () => {
  const [userRole, setUserRole] = useState(GetUserRole());
  const [isNavCollapsed, setIsNavCollapsed] = React.useState<boolean>(false); 
  const handleOnNavCollapsed = (isCollapsed: boolean): void => {
    setIsNavCollapsed(isCollapsed);
  };
  const isMobile = useIsMobile();
  const { themeMode } = useThemeMode();
  const location = useLocation();
  // Standalone/embedded mode: when the Release Management page is opened via a deep link
  // that carries the ReleaseVehicleId query parameter, hide the left navigation and render
  // the content full-width (header + footer are preserved).
  const isStandalone = new URLSearchParams(location.search).has("ReleaseVehicleId");
  const appStyles = getAppStyles(isNavCollapsed, themeMode, isStandalone);  
  
  useEffect(() => {
  const loadClarityScript = () => {
     if(isEmpty(process.env.REACT_APP_Clarity_ID)){
        return;
      }
    const clarityId = process.env.REACT_APP_Clarity_ID;
    if (isEmpty(clarityId)) return;

    const c = window as any;
    const l = document;
    const a = "clarity";
    const r = "script";

    if (!c[a]) {
      c[a] = function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    }

    const t: HTMLScriptElement = l.createElement(r);
    t.async = true;
    t.src = `https://www.clarity.ms/tag/${clarityId}`;

    const y: HTMLElement | null = l.getElementsByTagName(r)[0];
    if (y) {
      y.parentNode?.insertBefore(t, y);
    } else {
      l.head.appendChild(t);
    }
  };

  // Simulate consent check (replace with your actual CMP logic)
  const userHasConsented = () => {
    // Replace this with real consent check from your CMP
    return localStorage.getItem("clarityConsent") === "true";
  };

  if (userHasConsented()) {
    // Send consent signal before loading Clarity
    window.clarity?.('consentv2', {
      ad_storage: 'granted',
      analytics_storage: 'granted'
    });
    loadClarityScript();
  }

  // Optional: Listen for consent event from your CMP
  window.addEventListener("clarityConsentGranted", () => {
    window.clarity?.('consentv2', {
      ad_storage: 'granted',
      analytics_storage: 'granted'
    });
    loadClarityScript();
  });
   window.addEventListener("clarityConsentDeclined", () => {
      window.clarity?.('consentv2', {
            ad_Storage: "denied",
            analytics_Storage: "denied"
        }); 
    loadClarityScript();
  });
}, []);

  // if isMobile is true then collapse the left navigation
  useEffect(() => {   
    setIsNavCollapsed(isMobile);  
  }, [isMobile]);
   
  //below method is used to redirect bydefault screen based on role
  const userHasRole = () => {
    const roles = isEmpty(userRole) ? GetUserRole() : userRole;
    if (roles.includes(appRoles.Managed) || roles.includes(appRoles.Admin)) {
      return "/PORItems";
    }
    else if (roles.includes(appRoles.NonManaged)) {
      return "/NonManaged";
    } else if (roles.includes(appRoles.OLSOfferIDGeneration)) {
      return "/OLSOffer";
    } else if (roles.includes(appRoles.AuditData)) {
      return "/FinanceDataAudit";
    } else if (roles.includes(appRoles.Managed)) {
      return "/PORItems";
    }
  
    return "/PORItems"; // Default route if no match is found
  };
  

  return (
    <div className={appStyles.root}>
    
    <AuthenticatedTemplate> 
      <div className={appStyles.stickyHeader}>
        <AppHeader />
      </div> 
      {!isStandalone && <AppNavigation onNavCollapsed={handleOnNavCollapsed} isNavCollapsed={isNavCollapsed}/>}      
      <div className={appStyles.contentWrapper}>
      <main id="main" tabIndex={-1} className={appStyles.mainContainer}>
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}><CoherenceLoading primaryText="Loading..." /></div>}>
        <Routes>  
          <Route path="/" element={<Navigate   to={userHasRole()} />} />

           <Route path="/NonManaged"
                  element={<RouteGuard component={<NonManaged />} roles={[appRoles.NonManaged, appRoles.Accessibility,appRoles.Admin]}/>}/>
          <Route path="/PORItems"
                  element={<RouteGuard component={<PORItems />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>        
          <Route path="/CreatePOR" key="Create Por"
                  element={<RouteGuard component={<PORDetails key="add" />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
          <Route path="/PORDetails/:Id" key="Por Details"
                  element={<RouteGuard component={<PORDetails key="edit"/>} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>      
          <Route path="/ScopeDefinition/:Id"
                  element={<RouteGuard component={<ScopeDefinition />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
         <Route path="/ScopeDefinitionDetails/:Id"
                  element={<RouteGuard component={<ScopeDefinitionDetails />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
         <Route path="/DataMonitoring/:Id"
                  element={<RouteGuard component={<DataMonitoring />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
          <Route path="/SessionTimeout"
                  element={<SessionTimeout />}/>
          <Route path="/OLSOffer" key="OLS offerID Creation"
                  element={<RouteGuard component={<OLSOffer key="add" />} roles={[appRoles.OLSOfferIDGeneration,appRoles.Accessibility,appRoles.Admin]}/>}/>  
           <Route path="/FinanceDataAudit" key="Finance Data Audit"
                  element={<RouteGuard component={<FinanceDataAudit key="add" />} roles={[appRoles.AuditData,appRoles.Accessibility,appRoles.Admin]}/>}/>   
           <Route path="/VLSKUAudit" key="VL SKU Audit"
                  element={<RouteGuard component={<VLSKUAudit />} roles={[appRoles.AuditData,appRoles.Accessibility,appRoles.Admin]}/>}/>
           <Route path="/VLSKUAuditHistory" key="VL SKU Audit History"
                  element={<RouteGuard component={<VLSKUAuditHistory />} roles={[appRoles.AuditData,appRoles.Accessibility,appRoles.Admin]}/>}/>
           {/* Renamed from Finance Data Audit History; keep old bookmarks working. */}
           <Route path="/FinanceDataAuditHistory" key="Finance Data Audit History Redirect"
                  element={<Navigate to="/VLSKUAuditHistory" replace />}/>
          <Route path="/IntakeManagement" key="Intake Management"
                  element={<RouteGuard component={<IntakeManagement key="add" />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>      
          <Route path="/IntentInputCaptures" key="Intent Input Captures"
                  element={<RouteGuard component={<IntentInputCaptures key="add" />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
            <Route path="/IntentInputCaptureDetails" key="Intent Input Capture Details"
              element={<RouteGuard component={<IntentInputCaptureDetails />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
          <Route path="/ProductConfigurator" key="Product Configurator"
                  element={<RouteGuard component={<ProductConfiguratorList key="add" />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
          <Route path="/ProductConfiguratorDetails" key="Product Configurator Details"
                  element={<RouteGuard component={<ProductConfiguratorDetails />} roles={[appRoles.Managed, appRoles.Accessibility,appRoles.Admin]}/>}/>
          <Route path="/IntentValidations" key="Intent Validation Issues"
                  element={<RouteGuard component={<IntentValidations key="add" />} roles={[appRoles.Managed,appRoles.Accessibility,appRoles.Admin]}/>}/>   
          <Route path="/MetadataManagement" key="Metadata Management"
                  element={<RouteGuard component={<MetadataManagement key="add" />} roles={[appRoles.OpsAdmin]}/>}/>
            
          <Route path="/UnderDevelopment" key="UnderDevelopment"
                  element={<RouteGuard component={<UnderDevelopment key="UnderDevelopment" />} roles={[]}/>}/>  
          <Route path="/SearchResult" key="Search Result"
                  element={<RouteGuard component={<HeaderSearchResults key="add" />} roles={[appRoles.Managed, appRoles.Accessibility, appRoles.Admin]}/>}/>       
          <Route path="/Insights/:Id" key="Insights"
                  element={<RouteGuard component={<Insights/>} roles={[appRoles.Managed, appRoles.Accessibility, appRoles.Admin]}/>}/>       
          <Route path="/Publish/:Id" key="Publish"
                  element={<RouteGuard component={<Publish/>} roles={[appRoles.Managed, appRoles.Accessibility, appRoles.Admin]}/>}/>          
          <Route path="/ReleaseManagement" key="Release Management"
                  element={<RouteGuard component={<ReleaseManagement />} roles={[appRoles.ReleaseManagement, appRoles.Accessibility, appRoles.Admin]}/>}/>
          <Route path="/CDDeploymentSummary" key="CD Deployment Summary"
                  element={<RouteGuard component={<CDDeploymentSummary />} roles={[appRoles.ReleaseManagement, appRoles.Accessibility, appRoles.Admin]}/>}/>
          <Route path="/FixedEvidenceSummary" key="Fixed Evidence Summary"
                  element={<RouteGuard component={<FixedEvidenceSummary />} roles={[appRoles.ReleaseManagement, appRoles.Accessibility, appRoles.Admin]}/>}/>
         </Routes>
        </Suspense>
      </main>
         <div className={appStyles.footerStyle}>
         <AppFooter></AppFooter> 
         </div>
      </div>
    </AuthenticatedTemplate> 

    <CookieConsentBanner />

    </div>

  );
}

export default App;
